import React, { useId } from 'react';
import StuiFrame from './StuiFrame';

/**
 * A diagram: inline SVG in a StuiFrame captioned with its title, and a line under it saying what it
 * shows. Marks take the page's colours; `tone` on a mark carries its meaning — accent (the edge under
 * discussion), pass, fail, muted — so a diagram reads in every theme with no literal colour. The SVG
 * is drawn at its own size (text stays 12px) and scrolls sideways in a narrow window.
 *
 *   <StuiDiagram title="The Page Picks the Verb" label="..." caption="..." width={820} height={260}>
 *     {(arrow) => (<>
 *       <Box x={20} y={40} w={200} h={50} title="Engineering page" sub="nav row 'tags'" />
 *       <Arrow from={[220, 65]} to={[600, 120]} marker={arrow('muted')} tone="muted" label="writes" />
 *     </>)}
 *   </StuiDiagram>
 *
 * Children is a function handed `arrow(tone)`, which returns the marker reference for an arrowhead of
 * that tone; marker ids are unique per diagram so several can share a page. Anything after the
 * drawing (a paragraph of detail) goes in `after`.
 */
const TONES = ['plain', 'accent', 'pass', 'fail', 'muted'];
const LINE = 15;

export const StuiDiagram = ({ title, label, caption, width, height, after, children }) => {
  const id = useId().replace(/:/g, '');
  const arrow = (tone = 'plain') => `url(#${id}-${tone})`;
  return (
    <StuiFrame caption={title}>
      <div className="stui-diagram">
        <svg viewBox={`0 0 ${width} ${height}`} width={width} height={height} role="img" aria-label={label}>
          <defs>
            {TONES.map(t => (
              <marker key={t} id={`${id}-${t}`} data-tone={t === 'plain' ? undefined : t} viewBox="0 0 10 10" refX="9" refY="5"
                markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                <path d="M0,0 L10,5 L0,10 z" />
              </marker>
            ))}
          </defs>
          {children(arrow)}
        </svg>
      </div>
      {caption && <p className="stui-diagram-caption">{caption}</p>}
      {after}
    </StuiFrame>
  );
};

/** A box with a title and up to two muted lines under it, centred. */
export const Box = ({ x, y, w, h, title, sub, sub2, tone, dash }) => {
  const lines = [sub, sub2].filter(Boolean);
  const top = y + h / 2 - (lines.length * LINE) / 2 + 4;
  return (
    <g>
      <rect x={x} y={y} width={w} height={h} rx="6" data-tone={tone} data-dash={dash ? '' : undefined} />
      <text x={x + w / 2} y={top} textAnchor="middle" data-tone={tone}>{title}</text>
      {lines.map((l, i) => (
        <text key={i} x={x + w / 2} y={top + LINE + i * LINE} textAnchor="middle" data-tone={tone === 'accent' ? 'accent' : 'muted'}>{l}</text>
      ))}
    </g>
  );
};

/** A straight or elbowed arrow through `via` points, with an optional label at `labelAt` (default: the middle of the first leg). */
export const Arrow = ({ from, to, via = [], marker, tone, dash, label, labelAt, anchor = 'middle' }) => {
  const pts = [from, ...via, to];
  const [lx, ly] = labelAt || [(pts[0][0] + pts[1][0]) / 2, (pts[0][1] + pts[1][1]) / 2 - 6];
  return (
    <g>
      <polyline points={pts.map(p => p.join(',')).join(' ')} markerEnd={marker} data-tone={tone} data-dash={dash ? '' : undefined} />
      {label && <text x={lx} y={ly} textAnchor={anchor} data-tone={tone}>{label}</text>}
    </g>
  );
};

/** A free line of text. */
export const Note = ({ x, y, children, tone, anchor = 'start' }) => (
  <text x={x} y={y} textAnchor={anchor} data-tone={tone}>{children}</text>
);

export default StuiDiagram;
